<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useLocale from '../composables/useLocale';
import PlayPauseBtn from './Control/PlayPauseBtn.vue';
import AudioVolumeBtn from './Control/AudioVolumeBtn.vue';
import useMediaStore from '../stores/media';
import useUiStore from '../stores/ui';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faLockKeyhole, faLockKeyholeOpen, faVolumeHigh, faVolumeSlash } from '@fortawesome/pro-solid-svg-icons';
library.add(faLockKeyhole, faLockKeyholeOpen, faVolumeHigh, faVolumeSlash);

type Props = {
  showTooltips?: boolean;
};
withDefaults(defineProps<Props>(), {
  showTooltips: true,
});

const { platform } = useDisplay();
// Permanent solution to understand is it touchable device to show UI buttons :D, see category-ball/book-cover
const isTouchDevice = computed(() => platform.value.touch);

const { t } = useLocale();
const uiStore = useUiStore();
const { isLockScreenOverlayVisible, isControlsVisible, isChevronsVisible } = storeToRefs(uiStore);
const { toggleVisibleLockScreenOverlay } = uiStore;
const { playing, isPlaying } = storeToRefs(useMediaStore());

const interactionTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const ctrlVisible = ref<boolean>(false);
const isHovering = ref<boolean>(false);
const overlayContentRef = ref(null);

const handleTogglePlayer = () => {
  playing.value = !playing.value;
};

const onInteraction = () => {
  ctrlVisible.value = true;
  if (interactionTimeout.value) {
    clearTimeout(interactionTimeout.value);
  }
  interactionTimeout.value = setTimeout(() => {
    ctrlVisible.value = false;
  }, 3000);
};
const onUnlock = () => {
  toggleVisibleLockScreenOverlay(false);
  isControlsVisible.value = true;
  isChevronsVisible.value = true;
};

// @ts-ignore - this is the recommended way to import it - https://www.npmjs.com/package/@vueuse/integrations
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
const { activate: activateOverlayFocus, deactivate: deactivateOverlayFocus } = useFocusTrap(overlayContentRef, {
  escapeDeactivates: true,
});

watch(isLockScreenOverlayVisible, (visible) => {
  if (visible) {
    nextTick(() => {
      if (overlayContentRef.value) {
        activateOverlayFocus();
      }
    });
  } else {
    nextTick(() => {
      deactivateOverlayFocus();
    });
  }
});
</script>

<template>
  <v-overlay
    v-model="isLockScreenOverlayVisible"
    class="bg-transparent"
    opacity="0"
    :close-on-content-click="false"
    persistent
    absolute
    scrim="transparent"
    @click:outside="onInteraction"
    @mousemove="onInteraction"
    @touchmove="onInteraction"
    @keydown="onInteraction"
    @keyup.esc="onUnlock"
  >
    <div
      ref="overlayContentRef"
      class="overlay-content position-relative d-flex align-baseline justify-center"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      :aria-label="t('book_player.label_interactive_screen_lock_overlay')"
    >
      <div class="d-inline-flex overlay-buttons rounded-pill px-2 ma-3" :class="{ visible: ctrlVisible }">
        <v-btn
          id="unlock-screen-btn"
          color="on-primary"
          variant="text"
          :ripple="false"
          icon
          :aria-label="t('label_unlock_screen')"
          @mouseenter="isTouchDevice ? () => {} : (isHovering = true)"
          @mouseleave="isHovering = false"
          @blur="isHovering = false"
          @focus="isHovering = true"
          @touch="isHovering = false"
          @click="onUnlock"
          size="48"
        >
          <FontAwesomeIcon
            size="xl"
            :icon="isHovering ? 'fa-solid fa-lock-keyhole-open' : 'fa-solid fa-lock-keyhole'"
          />
          <v-tooltip :disabled="!showTooltips" activator="parent" location="bottom" :text="t('label_unlock_screen')" />
        </v-btn>
        <PlayPauseBtn
          :is-playing="isPlaying"
          icon-size="xl"
          :show-tooltips="showTooltips"
          @play-pause="handleTogglePlayer"
        />
        <AudioVolumeBtn icon-size="xl" btn-size="48" />
      </div>
    </div>
  </v-overlay>
</template>

<style scoped lang="scss">
.overlay-content {
  width: 100vw;
  width: 100dvw; // for mobile browsers
  height: 100vh;
  height: 100dvh; // for mobile browsers
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  color: rgb(var(--v-theme-background)) !important;
}

.overlay-buttons {
  opacity: 0;
  transition: opacity 0.2s ease;
  background-color: rgb(var(--v-theme-inverse-surface), 0.9);
  border: 1px solid rgb(var(--v-theme-outline-variant));
  outline: solid 1px rgb(var(--v-theme-outline-variant)) !important;
}
.overlay-buttons.visible {
  opacity: 1;
}
</style>
